import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { injectBxIcons, barChart05Icon, clockIcon, signal01Icon, thermometer01Icon, thermometer02Icon } from '@beyonnex/bx-angular-ui';
import { formatDateTime } from '../../../models/core.models';
import { CommonTranslateService } from '../../../services/common-translate/common-translate.service';
import { CurrentStatePanelComponent } from '../current-state-panel/current-state-panel.component';
import { CurrentStateDataPoint } from '../current-state-panel/current-state-panel.models';
import { DeviceDetailsStore } from '../device-details.store';

@Component({
  selector: 'bxi-qtemp-current-state',
  imports: [CurrentStatePanelComponent],
  templateUrl: './qtemp-current-state.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QtempCurrentStateComponent {
  private readonly store = inject(DeviceDetailsStore);
  protected readonly commonTranslate = inject(CommonTranslateService);

  protected readonly qtempDevice = toSignal(this.store.qtempDevice$);
  protected readonly gatewayLink = toSignal(this.store.gatewayLink$);
  protected readonly data = computed<CurrentStateDataPoint[]>(() => {
    const qtemp = this.qtempDevice();
    const readout = qtemp?.features?.readout?.properties;
    const grid = qtemp?.features?.grid?.properties;

    return [
      {
        label: this.commonTranslate.translateThingProperty('features.readout.properties.actual.temperature'),
        subLabel: this.commonTranslate.translateProbeMapping(qtemp?.getTemperatureMappingLabels()),
        value: this.commonTranslate.formatCelsius(readout?.actual?.temperature),
        icon: 'thermometer-01',
        iconStyle: 'text-utility-brand-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.readout.properties.daily.minTemperature'),
        value: this.commonTranslate.formatCelsius(readout?.daily?.minTemperature),
        icon: 'thermometer-02',
        iconStyle: 'text-utility-blue-light-600',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.readout.properties.daily.maxTemperature'),
        value: this.commonTranslate.formatCelsius(readout?.daily?.maxTemperature),
        icon: 'thermometer-02',
        iconStyle: 'text-utility-warning-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.rssi'),
        value: grid?.rssi,
        icon: 'bar-chart-05',
        iconStyle: 'text-teal-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.gateway'),
        value: grid?.gateway,
        icon: 'signal-01',
        iconStyle: 'text-utility-gray-blue-500',
        ...this.gatewayLink(),
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.receiveTime'),
        value: formatDateTime(grid?.receiveTime),
        icon: 'clock',
        iconStyle: 'text-utility-blue-light-600',
      },
    ];
  });

  constructor() {
    injectBxIcons([thermometer01Icon, thermometer02Icon, barChart05Icon, signal01Icon, clockIcon]);
  }
}
